import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { api } from '@/api'
import type { AssetItem, OutputKind } from '@/api/types'
import { classifyLocal } from '@/config/assetTaxonomy'
import { appSlugFromLocation, sourcePageFromLocation } from '@/lib/appSlug'
import { rememberAsset } from '@/lib/assetFallback'
import { imgHostLabel, toRelUrl } from '@/lib/mediaUrl'
import { useToast } from './ToastContext'

type AssetApi = {
  assets: AssetItem[]
  uploading: boolean
  upload: (files: File[]) => Promise<AssetItem[]>
  addGenerated: (item: AssetItem) => void
  remove: (id: string) => void
  /**
   * 抽屉开关归 Context 管，**不再由 AppShell 持有**。
   *
   * 原因是一个真实 bug：「从素材库选图」点了没反应。
   * 旧写法里 `picking` 在 Context、`open` 在 AppShell，两处状态互不相干 ——
   * `pickOne()` 把 picking 置了 true，可没有任何人去开抽屉，
   * 于是抽屉不出现、picking 还卡在 true。
   * 把「是否打开」和「是否在选择模式」放同一个地方，这个 bug 结构上就不可能再出现。
   */
  drawerOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
  /**
   * 开启「从素材库选择」模式（自动打开抽屉），返回被选中的素材（或 null 表示取消）。
   *
   * `kind` = **本次只许选这一种形态**（2026-10-09 加）。挑配音时必须传 `'audio'`：
   * 素材库是全站混装的，让用户在几百张图里翻出一段 mp3，翻到之后还可能点错一张图 ——
   * 而那个错会一路走到上游（「参考图」收到一个 mp3）。约束落在控件里，
   * 不靠调用方拿到结果之后再校验：那时用户已经白挑了一轮。
   */
  pickOne: (kind?: OutputKind) => Promise<AssetItem | null>
  /**
   * 多选模式。`limit` 为 0 表示不限；返回空数组代表取消。
   *
   * 单选与多选必须是**两个方法**而不是一个带开关的方法：
   * 调用方拿到的类型不同（`AssetItem` vs `AssetItem[]`），合并会让每个调用点都要判空。
   */
  pickMany: (limit?: number, kind?: OutputKind) => Promise<AssetItem[]>
  picking: boolean
  /** 本次选择被限定到哪一种形态（null = 不限，见 `pickOne`） */
  pickKind: OutputKind | null
  /** 'single' 点一下就收；'many' 点选累积，底部按钮确认 */
  pickMode: 'none' | 'single' | 'many'
  pickLimit: number
  resolvePick: (item: AssetItem | null) => void
  resolvePicks: (items: AssetItem[]) => void
}

const Ctx = createContext<AssetApi | null>(null)
const MIRROR_KEY = 'hw_mirror_assets_v1'

function loadMirror(): AssetItem[] {
  try {
    const raw = localStorage.getItem(MIRROR_KEY)
    // objectURL 形式的 mock 地址刷新后必然失效，直接过滤掉，不留死图
    const list = raw ? (JSON.parse(raw) as AssetItem[]) : []
    return list
      .filter((a) => !a.url.startsWith('blob:'))
      .map((a) => ({
        ...a,
        url: toRelUrl(a.url),
        thumbUrl: toRelUrl(a.thumbUrl),
        localUrl: a.localUrl ? toRelUrl(a.localUrl) : a.localUrl,
      }))
  } catch {
    return []
  }
}

function saveMirror(list: AssetItem[]) {
  try {
    localStorage.setItem(MIRROR_KEY, JSON.stringify(list.filter((a) => !a.url.startsWith('blob:')).slice(0, 100)))
  } catch {
    /* ignore */
  }
}

export function AssetLibraryProvider({ children }: { children: ReactNode }) {
  const [assets, setAssets] = useState<AssetItem[]>(() => loadMirror())
  const [uploading, setUploading] = useState(false)
  /*
   * 选择模式用三态而不是布尔 + 一个 limit。
   * 布尔的话，抽屉无法区分「点一下收工」和「点选累积等确认」这两种交互，
   * 结果就是多选模式下仍然点一下就关抽屉。
   */
  const [pickMode, setPickMode] = useState<'none' | 'single' | 'many'>('none')
  const [pickLimit, setPickLimit] = useState(0)
  /** 本次选择被限定到哪一种形态（null = 不限）。见 `pickOne` 上的注释 */
  const [pickKind, setPickKind] = useState<OutputKind | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const pickResolver = useRef<((items: AssetItem[]) => void) | null>(null)
  const toast = useToast()

  useEffect(() => {
    let alive = true
    api
      .listAssets()
      .then((remote) => {
        if (!alive || !remote.length) return
        // 归一化：老库里存的是 http://127.0.0.1:8000/static/... 绝对地址
        const clean = remote.map((r) => ({
          ...r,
          url: toRelUrl(r.url),
          thumbUrl: toRelUrl(r.thumbUrl),
          localUrl: r.localUrl ? toRelUrl(r.localUrl) : r.localUrl,
        }))
        setAssets((local) => {
          const map = new Map(local.map((a) => [a.id, a]))
          clean.forEach((r) => map.set(r.id, r))
          return Array.from(map.values())
        })
      })
      .catch(() => {
        /* 后端未就绪时静默降级到本地镜像 */
      })
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => saveMirror(assets), [assets])

  // 登记图床地址 → 本地副本 的兜底映射：图床图加载失败时 img 自动换本地副本
  useEffect(() => {
    assets.forEach(rememberAsset)
  }, [assets])

  const upload = useCallback(
    async (files: File[]) => {
      if (!files.length) return []
      setUploading(true)
      const added: AssetItem[] = []
      let mirrored = 0
      try {
        // 预检：先确认后端活着，再动真格传文件。
        // 上传的超时必须设得很长（后端自己发 COS 就要等 300s），没有这一步，
        // 后端僵死时用户会盯着转圈的框等满几分钟，而真实原因是「后端没响应」。
        // 健康检查是毫秒级的纯内存计算，加它几乎零成本。
        await api.ping()

        // 上传素材的类别由「在哪传的」决定 —— 从当前路由反推，页面无需逐个上报
        const context = appSlugFromLocation()
        for (const file of files) {
          const res = await api.upload(file)
          if (res.host) mirrored += 1
          const kind: AssetItem['kind'] = file.type.startsWith('video')
            ? 'video'
            : file.type.startsWith('audio')
              ? 'audio'
              : 'image'
          const item: AssetItem = {
            id: `asset-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
            url: res.downloadUrl,
            localUrl: res.localUrl,
            thumbUrl: res.downloadUrl,
            name: res.fileName,
            kind,
            size: res.size,
            width: res.width,
            height: res.height,
            createdAt: new Date().toISOString(),
            source: 'upload',
            host: res.host,
            context,
            assetCategory: classifyLocal(context, kind, sourcePageFromLocation()),
          }
          added.push(item)
          await api.addAsset(item).catch(() => undefined)
        }
        setAssets((list) => [...added, ...list])
        // 图床成功与否要如实说：失败时图仍在，但只在本地，跨设备/给上游用会退化
        if (mirrored === added.length && added.length) {
          toast.success(`已加入素材库 ${added.length} 个文件（图床 ${imgHostLabel(added[0]?.host ?? '')}）`)
        } else if (mirrored === 0 && added.length) {
          toast.success(`已加入素材库 ${added.length} 个文件（图床不可用，暂存本地）`)
        } else {
          toast.success(`已加入素材库 ${added.length} 个文件（${mirrored} 个已入图床）`)
        }
        return added
      } catch (e) {
        toast.error(e instanceof Error ? e.message : '上传失败')
        return added
      } finally {
        setUploading(false)
      }
    },
    [toast],
  )

  /**
   * 登记一件产出/素材。
   *
   * 🔴 **同一个 id 只留一条**（2026-10-09 加）。加之前是「无脑前插」，
   * 于是这两条路会各插一条同样的东西：
   *   · 音频产出的**自动入库**（见 `JobCenterContext`）与结果卡上的「存素材」；
   *   · 同一张图连着点两次「存素材」。
   * 去重**放在这里而不是每个调用点**：调用点有五六处，
   * 「哪一处忘了判重」在界面上只表现为「资产库里怎么有两份」——
   * 这种漂移本项目有前科（同一个规则两处各写一份）。
   *
   * 后端那一侧本来就是 `INSERT OR REPLACE`（按 id 覆盖），所以幂等只差这一层；
   * 调用方因此可以放心地「按同一个 id 反复登记」而不用自己记状态。
   */
  const addGenerated = useCallback((item: AssetItem) => {
    /**
     * 🔴 **不许对内存里这份列表做 `slice(0, 100)`**（2026-10-09 删掉的那一句）。
     *
     * 原句是 `[item, ...list].slice(0, 100)`，看着像个无害的上限，实际是
     * **静默隐藏数据**：页面加载时 `listAssets()` 会把库里**全部**素材并进来
     * （实测 173 条），此后只要有任何一次入库，列表就被截回 100 条 ——
     * 73 件素材**从界面上消失**，而库里一条没少、后端也不报错。
     *
     * 触发它最容易的一条路正是**音频产出的自动入库**（每合成一条语音就调这里）：
     * 用户跑完一次语音合成，素材库计数从 173 掉到 100，看起来像「东西被删了」。
     *
     * 那个 100 真正该待的地方只有 `saveMirror()`（localStorage 有容量限制，
     * 且它只是**离线兜底**，在线时以服务端返回为准）。内存这份是事实的镜像，
     * 上限就等于「用户能看到多少」，不许由我们随手定。
     */
    setAssets((list) => (list.some((a) => a.id === item.id) ? list : [item, ...list]))
    api.addAsset(item).catch(() => undefined)
  }, [])

  const remove = useCallback((id: string) => {
    setAssets((list) => list.filter((a) => a.id !== id))
    api.removeAsset(id).catch(() => undefined)
  }, [])

  const pickOne = useCallback(
    (kind?: OutputKind) =>
      new Promise<AssetItem | null>((resolve) => {
        pickResolver.current = (items) => resolve(items[0] ?? null)
        setPickMode('single')
        setPickLimit(1)
        setPickKind(kind ?? null)
        // 关键：选择模式**必然**要能看到素材，所以顺手把抽屉打开。
        // 以前少了这一句，「从素材库选图」就是个无声的死按钮。
        setDrawerOpen(true)
      }),
    [],
  )

  const pickMany = useCallback(
    (limit = 0, kind?: OutputKind) =>
      new Promise<AssetItem[]>((resolve) => {
        pickResolver.current = resolve
        setPickMode('many')
        setPickLimit(limit)
        setPickKind(kind ?? null)
        setDrawerOpen(true)
      }),
    [],
  )

  const resolvePicks = useCallback((items: AssetItem[]) => {
    pickResolver.current?.(items)
    pickResolver.current = null
    setPickMode('none')
    setPickLimit(0)
    // 形态限制必须跟着一起清 —— 留着的话，下一次在别处「从素材库选图」
    // 会继承上一次那个 `'audio'`，用户看到的是一个只剩配音的空抽屉。
    setPickKind(null)
    // 选完/取消都要关抽屉，否则下一次在别处打开时还停在「选择素材」标题上
    setDrawerOpen(false)
  }, [])

  const resolvePick = useCallback(
    (item: AssetItem | null) => resolvePicks(item ? [item] : []),
    [resolvePicks],
  )

  const openDrawer = useCallback(() => setDrawerOpen(true), [])

  const closeDrawer = useCallback(() => {
    // 关抽屉等价于取消选择 —— 否则 pickOne/pickMany 的 Promise 永远不 resolve，
    // 调用方那个 await 就一直挂着
    if (pickResolver.current) {
      pickResolver.current([])
      pickResolver.current = null
      setPickMode('none')
      setPickLimit(0)
      setPickKind(null)
    }
    setDrawerOpen(false)
  }, [])

  const picking = pickMode !== 'none'

  const value = useMemo<AssetApi>(
    () => ({
      assets,
      uploading,
      upload,
      addGenerated,
      remove,
      drawerOpen,
      openDrawer,
      closeDrawer,
      pickOne,
      pickMany,
      picking,
      pickMode,
      pickLimit,
      pickKind,
      resolvePick,
      resolvePicks,
    }),
    [
      assets,
      uploading,
      upload,
      addGenerated,
      remove,
      drawerOpen,
      openDrawer,
      closeDrawer,
      pickOne,
      pickMany,
      picking,
      pickMode,
      pickLimit,
      pickKind,
      resolvePick,
      resolvePicks,
    ],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAssetLibrary() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAssetLibrary must be used within AssetLibraryProvider')
  return ctx
}
